import Vue from "vue";
import { mapGetters } from "vuex";
import { LoadingSpinner } from "@/components";
import { themeHelper } from "@/components/mixins/themeHelper";

/**
 * DiagramViewer – Displays an interactive parts diagram image with
 * Ctrl+Scroll zoom and click-and-drag panning. Shows contextual empty
 * states when no diagram URL is available or no results are found.
 */
export default Vue.extend({
    name: "DiagramViewer",
    components: {
        LoadingSpinner,
    },
    mixins: [
        themeHelper
    ],
    props: {
        /** Whether the diagram data is still loading. */
        loading: {
            type: Boolean,
            default: false
        },
        /** URL (or data-URI) of the diagram image to render. */
        diagramUrl: {
            type: String,
            default: ""
        },
        /** Alt text for the diagram image. */
        diagramAltText: {
            type: String,
            default: ""
        },
        /** Whether the parent search returned results. */
        hasResults: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        /** Current zoom multiplier (1 = 100%). Clamped between 0.5 and 3. */
        zoomLevel: 1,
        isDragging: false,
        dragStartX: 0,
        dragStartY: 0,
        scrollStartX: 0,
        scrollStartY: 0,
    }),
    computed: {
        ...mapGetters([
            "getContentItem",
        ]),
    },
    methods: {
        /**
         * Handles Ctrl+Scroll zoom. Adjusts zoom level and repositions the
         * scroll so the content point under the cursor stays fixed.
         */
        handleWheel(event: WheelEvent) {
            if (event.ctrlKey) {
                event.preventDefault();
                const wrapper = this.$refs.diagramWrapper as HTMLElement;
                if (!wrapper) return;

                // Cursor position within the wrapper viewport
                const rect = wrapper.getBoundingClientRect();
                const cursorX = event.clientX - rect.left;
                const cursorY = event.clientY - rect.top;

                // Point in content-space the cursor is over
                const contentX = wrapper.scrollLeft + cursorX;
                const contentY = wrapper.scrollTop + cursorY;

                // Ratio of cursor position within the full scrollable content
                const ratioX = contentX / wrapper.scrollWidth;
                const ratioY = contentY / wrapper.scrollHeight;

                if (event.deltaY < 0) {
                    this.zoomLevel = Math.min(this.zoomLevel + 0.1, 3);
                } else {
                    this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.5);
                }

                // After Vue re-renders with the new width, adjust scroll so the
                // same content point stays under the cursor.
                this.$nextTick(() => {
                    const newContentX = ratioX * wrapper.scrollWidth;
                    const newContentY = ratioY * wrapper.scrollHeight;
                    wrapper.scrollLeft = newContentX - cursorX;
                    wrapper.scrollTop = newContentY - cursorY;
                });
            }
        },
        /** Begin panning: record initial mouse and scroll positions. */
        startDrag(event: MouseEvent) {
            this.isDragging = true;
            this.dragStartX = event.clientX;
            this.dragStartY = event.clientY;
            const wrapper = this.$refs.diagramWrapper as HTMLElement;
            if (wrapper) {
                this.scrollStartX = wrapper.scrollLeft;
                this.scrollStartY = wrapper.scrollTop;
            }
        },
        /** Continue panning while mouse is held down. */
        onDrag(event: MouseEvent) {
            if (!this.isDragging) return;
            event.preventDefault();
            const wrapper = this.$refs.diagramWrapper as HTMLElement;
            if (!wrapper) return;
            wrapper.scrollLeft = this.scrollStartX - (event.clientX - this.dragStartX);
            wrapper.scrollTop = this.scrollStartY - (event.clientY - this.dragStartY);
        },
        /** Stop panning on mouse-up or mouse-leave. */
        endDrag() {
            this.isDragging = false;
        }
    }
});
